import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  MessageCircle, Search, Phone, Send, Clock, CheckCheck, Check,
  ChevronRight, ChevronLeft, Plus, User, Filter, FileText, Calendar,
  ArrowRight, Loader2, AlertCircle, X, Pencil, Trash2
} from 'lucide-react';

const WhatsApp = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [waNumbers, setWaNumbers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState('all'); // all, unread, mine
  const [filterEmployee, setFilterEmployee] = useState('all');

  // Chat input
  const [messageText, setMessageText] = useState('');
  const [messageDirection, setMessageDirection] = useState('outbound');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Dialogs
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showAddNumber, setShowAddNumber] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);

  // New convo form
  const [leads, setLeads] = useState([]);
  const [newConvoLeadId, setNewConvoLeadId] = useState('');
  const [newConvoNumberId, setNewConvoNumberId] = useState('');

  // New number form
  const [newNumberPhone, setNewNumberPhone] = useState('');
  const [newNumberName, setNewNumberName] = useState('');
  const [newNumberVertical, setNewNumberVertical] = useState('general');
  const [newNumberUserId, setNewNumberUserId] = useState('');
  const [agents, setAgents] = useState([]);

  // Follow-up
  const [followUpTitle, setFollowUpTitle] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');

  // Template editing (admin)
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditTemplate, setShowEditTemplate] = useState(false);
  const [editTmplName, setEditTmplName] = useState('');
  const [editTmplCategory, setEditTmplCategory] = useState('general');
  const [editTmplBody, setEditTmplBody] = useState('');

  // Mobile view
  const [mobileView, setMobileView] = useState('list'); // list, chat

  // Fetch data
  const fetchAll = useCallback(async () => {
    try {
      const [convRes, numRes, tmplRes, statsRes] = await Promise.all([
        api.get('/whatsapp/conversations'),
        api.get('/whatsapp/numbers'),
        api.get('/whatsapp/templates'),
        api.get('/whatsapp/stats'),
      ]);
      setConversations(convRes.data);
      setWaNumbers(numRes.data);
      setTemplates(tmplRes.data);
      setStats(statsRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Handle prefill from property share navigation
  useEffect(() => {
    const convoId = searchParams.get('convo');
    const prefill = searchParams.get('prefill');
    if (convoId && conversations.length > 0) {
      const convo = conversations.find(c => c.id === convoId);
      if (convo) {
        openConversation(convo);
        if (prefill) {
          setMessageText(decodeURIComponent(prefill));
        }
      }
      setSearchParams({}, { replace: true });
    }
  }, [conversations, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMessages = async (convoId) => {
    try {
      const res = await api.get(`/whatsapp/conversations/${convoId}/messages`);
      setMessages(res.data);
      // Mark as read
      api.post(`/whatsapp/conversations/${convoId}/mark-read`).catch(() => {});
    } catch { /* ignore */ }
  };

  const openConversation = (convo) => {
    setSelectedConvo(convo);
    fetchMessages(convo.id);
    setMobileView('chat');
  };

  // Filtered conversations
  const filteredConvos = conversations.filter(c => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!c.lead_name?.toLowerCase().includes(term) && !c.lead_phone?.includes(term)) return false;
    }
    if (filterTab === 'unread' && c.unread_count === 0) return false;
    if (filterTab === 'mine' && c.crm_user_id !== user?.id) return false;
    if (filterEmployee !== 'all' && c.crm_user_id !== filterEmployee) return false;
    return true;
  });

  // Send message
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConvo) return;
    setSendingMessage(true);
    try {
      const payload = {
        body: messageText,
        direction: messageDirection,
        message_type: 'text',
        source: 'manual',
      };
      if (showFollowUp && followUpTitle) {
        payload.follow_up = { title: followUpTitle, due_date: followUpDate || undefined };
      }
      await api.post(`/whatsapp/conversations/${selectedConvo.id}/messages`, payload);
      setMessageText('');
      setShowFollowUp(false);
      setFollowUpTitle('');
      setFollowUpDate('');
      fetchMessages(selectedConvo.id);
      fetchAll();
      toast.success('Message logged');
    } catch (e) {
      toast.error('Failed to log message');
    }
    setSendingMessage(false);
  };

  // Apply template
  const applyTemplate = (tmpl) => {
    let body = tmpl.body;
    // Auto-fill known variables
    const vars = {
      customer_name: selectedConvo?.lead_name || '',
      agent_name: user?.name || '',
    };
    for (const [k, v] of Object.entries(vars)) {
      body = body.replace(`{{${k}}}`, v);
    }
    setMessageText(body);
    setShowTemplate(false);
  };

  // Create conversation
  const handleCreateConvo = async () => {
    if (!newConvoLeadId || !newConvoNumberId) { toast.error('Select a lead and WhatsApp number'); return; }
    try {
      const res = await api.post('/whatsapp/conversations', { lead_id: newConvoLeadId, whatsapp_number_id: newConvoNumberId });
      setShowNewConvo(false);
      openConversation(res.data);
      fetchAll();
      toast.success('Conversation started');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Add number
  const handleAddNumber = async () => {
    if (!newNumberPhone) { toast.error('Phone number required'); return; }
    try {
      await api.post('/whatsapp/numbers', {
        phone_number: newNumberPhone, display_name: newNumberName || newNumberPhone,
        business_vertical: newNumberVertical, crm_user_id: newNumberUserId || undefined,
      });
      setShowAddNumber(false);
      setNewNumberPhone(''); setNewNumberName('');
      fetchAll();
      toast.success('WhatsApp number added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Template management (admin)
  const openEditTemplate = (tmpl) => {
    setEditingTemplate(tmpl);
    setEditTmplName(tmpl.name);
    setEditTmplCategory(tmpl.category);
    setEditTmplBody(tmpl.body);
    setShowEditTemplate(true);
  };

  const handleSaveTemplate = async () => {
    if (!editTmplName || !editTmplBody) { toast.error('Name and body required'); return; }
    try {
      if (editingTemplate) {
        await api.put(`/whatsapp/templates/${editingTemplate.id}`, { name: editTmplName, category: editTmplCategory, body: editTmplBody, variables: (editTmplBody.match(/\{\{(\w+)\}\}/g) || []).map(v => v.replace(/[{}]/g, '')) });
        toast.success('Template updated');
      } else {
        await api.post('/whatsapp/templates', { name: editTmplName, category: editTmplCategory, body: editTmplBody, variables: (editTmplBody.match(/\{\{(\w+)\}\}/g) || []).map(v => v.replace(/[{}]/g, '')) });
        toast.success('Template created');
      }
      setShowEditTemplate(false);
      setEditingTemplate(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteTemplate = async (tmplId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/whatsapp/templates/${tmplId}`);
      fetchAll();
      toast.success('Template deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Load leads for new convo dialog
  const openNewConvoDialog = async () => {
    try {
      const res = await api.get('/leads');
      setLeads(res.data);
    } catch { /* ignore */ }
    try {
      const res = await api.get('/team/agents');
      setAgents(res.data);
    } catch { /* ignore */ }
    setShowNewConvo(true);
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString('en-IN', { weekday: 'short' });
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const getStatusIcon = (status) => {
    if (status === 'read') return <CheckCheck className="w-3 h-3 text-blue-500" />;
    if (status === 'delivered') return <CheckCheck className="w-3 h-3 text-slate-400" />;
    if (status === 'sent') return <Check className="w-3 h-3 text-slate-400" />;
    if (status === 'failed') return <AlertCircle className="w-3 h-3 text-red-500" />;
    return <Clock className="w-3 h-3 text-slate-300" />;
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div></Layout>;

  return (
    <Layout>
    <div className="max-w-full overflow-x-hidden" data-testid="whatsapp-module">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3 px-1">
        <div>
          <h1 className="aavaas-page-title" data-testid="whatsapp-title">WhatsApp</h1>
          <p className="text-sm text-slate-500">
            {stats.active_numbers || 0} numbers · {stats.total_conversations || 0} conversations · {stats.unread_conversations || 0} unread
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => { api.get('/team/agents').then(r => setAgents(r.data)).catch(() => {}); setShowAddNumber(true); }} data-testid="add-number-btn">
              <Phone className="w-4 h-4 mr-1.5" /> Add Number
            </Button>
          )}
          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={openNewConvoDialog} data-testid="new-conversation-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Chat
          </Button>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex gap-4 h-[calc(100vh-200px)] min-h-[500px]">
        {/* Conversation List */}
        <div className={`${selectedConvo && mobileView === 'chat' ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-[380px] lg:flex-shrink-0 bg-white rounded-xl shadow-sm overflow-hidden`}>
          {/* Tabs */}
          <div className="flex border-b border-slate-100 px-2 pt-2">
            {['all', 'unread', 'mine'].map(tab => (
              <button key={tab} className={`flex-1 py-2 text-xs font-medium rounded-t-lg transition-colors ${filterTab === tab ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setFilterTab(tab)} data-testid={`tab-${tab}`}>
                {tab === 'all' ? 'All' : tab === 'unread' ? `Unread (${stats.unread_conversations || 0})` : 'My Chats'}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search conversations..." className="pl-10 h-9 bg-slate-50 border-0 text-sm" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="wa-search" />
            </div>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {filteredConvos.length === 0 ? (
              <div className="text-center py-12 px-4">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                <p className="text-sm font-medium text-slate-500">No conversations yet</p>
                <p className="text-xs text-slate-400 mt-1">Start a new chat with a lead</p>
              </div>
            ) : (
              filteredConvos.map(c => (
                <div key={c.id} className={`flex items-start gap-3 px-3 py-3 cursor-pointer border-b border-slate-50 transition-colors ${selectedConvo?.id === c.id ? 'bg-green-50' : 'hover:bg-slate-50'}`} onClick={() => openConversation(c)} data-testid={`convo-${c.id}`}>
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-green-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-900 truncate">{c.lead_name || c.lead_phone}</span>
                      <span className="text-[10px] text-slate-400 ml-2 flex-shrink-0">{formatTime(c.last_message_at)}</span>
                    </div>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_message || 'No messages yet'}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[10px] text-slate-400">{c.crm_user_name}</span>
                      {c.unread_count > 0 && <span className="ml-auto bg-green-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{c.unread_count}</span>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Panel */}
        <div className={`${!selectedConvo || mobileView !== 'chat' ? 'hidden lg:flex' : 'flex'} flex-col flex-1 bg-white rounded-xl shadow-sm overflow-hidden`}>
          {!selectedConvo ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-slate-200" />
                <p className="text-lg font-medium text-slate-400">Select a conversation</p>
                <p className="text-sm text-slate-300 mt-1">or start a new chat</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
                <button className="lg:hidden p-1" onClick={() => { setMobileView('list'); setSelectedConvo(null); }}>
                  <ChevronLeft className="w-5 h-5 text-slate-600" />
                </button>
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-green-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{selectedConvo.lead_name}</p>
                  <p className="text-[11px] text-slate-500">{selectedConvo.lead_phone} · {selectedConvo.crm_user_name}</p>
                </div>
                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-full border border-amber-200">Manual Log</span>
              </div>

              {/* Messages — anchored to bottom like real chat apps */}
              <div className="flex-1 overflow-y-auto p-4 flex flex-col justify-end bg-slate-50/50" data-testid="chat-messages">
                {messages.length === 0 && !messageText && (
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-400">No messages yet. Log your first interaction.</p>
                  </div>
                )}
                <div className="space-y-3">
                  {messages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${msg.direction === 'outbound' ? 'bg-green-600 text-white rounded-br-md' : 'bg-white text-slate-900 shadow-sm rounded-bl-md'}`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                        <div className={`flex items-center gap-1 mt-1 ${msg.direction === 'outbound' ? 'justify-end' : ''}`}>
                          {msg.source === 'manual' && <span className={`text-[9px] ${msg.direction === 'outbound' ? 'text-green-200' : 'text-slate-400'}`}>Logged</span>}
                          <span className={`text-[10px] ${msg.direction === 'outbound' ? 'text-green-200' : 'text-slate-400'}`}>{formatTime(msg.timestamp)}</span>
                          {msg.direction === 'outbound' && getStatusIcon(msg.status)}
                        </div>
                        {msg.follow_up_task_id && <p className={`text-[10px] mt-1 ${msg.direction === 'outbound' ? 'text-green-200' : 'text-blue-500'}`}>Follow-up created</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Follow-up bar */}
              {showFollowUp && (
                <div className="px-3 py-1.5 bg-blue-50 border-t border-blue-100 flex items-center gap-2 flex-wrap">
                  <Calendar className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                  <Input placeholder="Follow-up title" className="h-7 text-xs flex-1 min-w-[100px] bg-white" value={followUpTitle} onChange={(e) => setFollowUpTitle(e.target.value)} />
                  <Input type="date" className="h-7 text-xs w-[120px] bg-white" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} />
                  <button className="text-slate-400 hover:text-slate-600" onClick={() => setShowFollowUp(false)}><X className="w-3.5 h-3.5" /></button>
                </div>
              )}

              {/* Compact composer */}
              <div className="px-3 py-2 border-t border-slate-100">
                {/* Action bar — always compact */}
                <div className="flex items-center gap-3 mb-1.5">
                  <Select value={messageDirection} onValueChange={setMessageDirection}>
                    <SelectTrigger className="w-[90px] h-6 text-[11px] border-0 bg-transparent px-1 text-slate-500 hover:text-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="outbound">Sent</SelectItem>
                      <SelectItem value="inbound">Received</SelectItem>
                    </SelectContent>
                  </Select>
                  <button className="text-[11px] text-slate-500 hover:text-green-600 transition-colors" onClick={() => setShowTemplate(true)} data-testid="use-template-btn">
                    <FileText className="w-3 h-3 inline mr-0.5 -mt-px" />Template
                  </button>
                  <button className={`text-[11px] transition-colors ${showFollowUp ? 'text-blue-600' : 'text-slate-500 hover:text-blue-600'}`} onClick={() => setShowFollowUp(!showFollowUp)}>
                    <Calendar className="w-3 h-3 inline mr-0.5 -mt-px" />Follow-up
                  </button>
                  {/* Personalize hint — only when message has multi-line content (e.g. property share) */}
                  {messageText.split('\n').length > 3 && (
                    <span className="ml-auto text-[11px] text-green-600 flex items-center gap-0.5">
                      <Pencil className="w-3 h-3" /> Personalize
                    </span>
                  )}
                </div>
                {/* Editor + send */}
                <div className="flex items-end gap-2">
                  <div className="flex-1 min-w-0 relative">
                    <Textarea
                      placeholder="Log a message..."
                      className={`w-full bg-slate-50 border border-slate-200 focus:border-green-400 resize-none text-sm rounded-lg transition-all ${messageText.split('\n').length > 3 ? 'min-h-[180px] max-h-[45vh]' : 'min-h-[38px] max-h-[120px]'}`}
                      rows={messageText.split('\n').length > 3 ? Math.min(12, messageText.split('\n').length) : messageText.includes('\n') ? Math.min(4, messageText.split('\n').length) : 1}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSendMessage())}
                      data-testid="wa-message-input"
                    />
                    {messageText.length > 50 && (
                      <span className="absolute bottom-1.5 right-2.5 text-[10px] text-slate-400">{messageText.length}/2000</span>
                    )}
                  </div>
                  <Button className="bg-green-600 hover:bg-green-700 h-9 w-9 p-0 flex-shrink-0 rounded-full" onClick={handleSendMessage} disabled={sendingMessage || !messageText.trim()} data-testid="wa-send-btn">
                    {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={showNewConvo} onOpenChange={setShowNewConvo}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Start WhatsApp Conversation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Lead</Label>
              <Select value={newConvoLeadId} onValueChange={setNewConvoLeadId}>
                <SelectTrigger><SelectValue placeholder="Choose a lead..." /></SelectTrigger>
                <SelectContent>
                  {leads.map(l => <SelectItem key={l.id} value={l.id}>{l.name} — {l.phone}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>WhatsApp Number</Label>
              <Select value={newConvoNumberId} onValueChange={setNewConvoNumberId}>
                <SelectTrigger><SelectValue placeholder="Choose number..." /></SelectTrigger>
                <SelectContent>
                  {waNumbers.map(n => <SelectItem key={n.id} value={n.id}>{n.display_name} ({n.phone_number})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full bg-green-600 hover:bg-green-700" onClick={handleCreateConvo} data-testid="start-convo-btn">
              <MessageCircle className="w-4 h-4 mr-2" /> Start Conversation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Selector Dialog */}
      <Dialog open={showTemplate} onOpenChange={setShowTemplate}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Message Templates</DialogTitle>
              {isAdmin && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditingTemplate(null); setEditTmplName(''); setEditTmplCategory('general'); setEditTmplBody(''); setShowEditTemplate(true); setShowTemplate(false); }} data-testid="create-template-btn">
                  <Plus className="w-3 h-3 mr-1" /> New
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="space-y-2">
            {templates.map(t => (
              <div key={t.id} className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group" data-testid={`template-${t.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-900 cursor-pointer flex-1" onClick={() => applyTemplate(t)}>{t.name}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-slate-400 capitalize mr-1">{t.category}</span>
                    {isAdmin && (
                      <>
                        <button className="p-1 rounded hover:bg-slate-200 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); openEditTemplate(t); setShowTemplate(false); }} data-testid={`edit-template-${t.id}`}>
                          <Pencil className="w-3 h-3 text-slate-500" />
                        </button>
                        <button className="p-1 rounded hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t.id); }} data-testid={`delete-template-${t.id}`}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-600 line-clamp-2 cursor-pointer" onClick={() => applyTemplate(t)}>{t.body}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit/Create Template Dialog */}
      <Dialog open={showEditTemplate} onOpenChange={setShowEditTemplate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Name</Label>
              <Input value={editTmplName} onChange={e => setEditTmplName(e.target.value)} placeholder="e.g., Follow Up" data-testid="template-name-input" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={editTmplCategory} onValueChange={setEditTmplCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="enquiry">Enquiry</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="site_visit">Site Visit</SelectItem>
                  <SelectItem value="pricing">Pricing</SelectItem>
                  <SelectItem value="property_share">Property Share</SelectItem>
                  <SelectItem value="booking">Booking</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message Body</Label>
              <Textarea value={editTmplBody} onChange={e => setEditTmplBody(e.target.value)} rows={4} placeholder="Hi {{customer_name}}, ..." data-testid="template-body-input" />
              <p className="text-[10px] text-slate-400 mt-1">Use {'{{variable_name}}'} for placeholders</p>
            </div>
            <Button className="w-full bg-green-600 hover:bg-green-700" onClick={handleSaveTemplate} data-testid="save-template-btn">
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Number Dialog */}
      <Dialog open={showAddNumber} onOpenChange={setShowAddNumber}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add WhatsApp Number</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Phone Number *</Label>
              <Input placeholder="+919876543210" value={newNumberPhone} onChange={(e) => setNewNumberPhone(e.target.value)} />
            </div>
            <div>
              <Label>Display Name</Label>
              <Input placeholder="e.g., Sanjeev - Plots" value={newNumberName} onChange={(e) => setNewNumberName(e.target.value)} />
            </div>
            <div>
              <Label>Business Vertical</Label>
              <Select value={newNumberVertical} onValueChange={setNewNumberVertical}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="plots">Plots</SelectItem>
                  <SelectItem value="flats">Flats</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign to Employee (optional)</Label>
              <Select value={newNumberUserId} onValueChange={setNewNumberUserId}>
                <SelectTrigger><SelectValue placeholder="Select employee..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">Unassigned</SelectItem>
                  {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full bg-green-600 hover:bg-green-700" onClick={handleAddNumber} data-testid="save-number-btn">
              <Phone className="w-4 h-4 mr-2" /> Add Number
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default WhatsApp;
